import type { TableCellMenuUiProps } from '@shared/rich-text-editor-core';
import React, { useEffect, useRef } from 'react';

export default function TableCellMenu({
  isOpen,
  onOpen,
  onClose,
  onSelect,
  items,
  className,
}: Readonly<TableCellMenuUiProps>) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside trigger or popover
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current && triggerRef.current.contains(target)) return;
      if (popoverRef.current && popoverRef.current.contains(target)) return;
      onClose();
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [isOpen, onClose]);

  return (
    <div className={`${className ?? ''} ${isOpen ? 'table-cell-menu-open' : ''}`}>
      <button
        type='button'
        ref={triggerRef}
        className='table-cell-menu-trigger absolute top-1 right-1 inline-flex h-6 w-6 items-center justify-center rounded bg-white/90 text-neutral-700 shadow hover:bg-white'
        onClick={(e) => {
          e.stopPropagation();
          onOpen(e);
        }}
        aria-haspopup='menu'
        aria-expanded={isOpen}
        aria-label='Cell menu'
      >
        <svg
          viewBox='0 0 24 24'
          width='1em'
          height='1em'
          stroke='currentColor'
          strokeWidth={1.5}
          strokeLinecap='round'
          strokeLinejoin='round'
          fill='none'
        >
          <path d='M6 9l6 6 6-6' />
        </svg>
      </button>

      {isOpen ? (
        <div
          role='menu'
          ref={popoverRef}
          tabIndex={0}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
            e.stopPropagation();
          }}
          className='table-cell-menu-popover absolute top-7 right-1 z-20 mt-1 min-w-[200px] rounded border border-neutral-200 bg-white p-1 shadow'
        >
          <div className='flex flex-col'>
            {items.map((item) => (
              <button
                key={item.key}
                type='button'
                className='table-cell-menu-item w-full rounded px-2 py-1.5 text-left text-sm hover:bg-neutral-100'
                onClick={() => onSelect(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
